import utils from '../utils';

export interface RejectDuplicateOptions {
  equals?(a: any, b: any): boolean;
  adjacentEquality?: ((a: any, b: any) => boolean) | string;
  ignoreOctave?: boolean;
  debug?: boolean;
}

const debug = false;

/**
 * rejectDuplicates on an array of questions and return enough questions
 * with as little duplication as possible, and nicely spread-out repetition
 * if there is repetition.
 * @param {any[]} array of questions
 * @param {number} numberOfQuestions
 * @param {Object} options
 * @param {Function} options.equals comparator for uniqueness
 * @param {Function} options.adjacentEquality comparator for adjacency
 * @param {boolean} [options.ignoreOctave] if question objects have an
 *  equalsIgnoreOctave function, use that to compare.
 * @returns {any[]} an array of questions
 */
export default function rejectDuplicates<T>(
  array: T[],
  numberOfQuestions: number,
  options: RejectDuplicateOptions = {}
): T[] {
  function log(...args): void {
    if (options.debug || debug) {
      console.log(...args);
    }
  }

  function equals(a: any, b: any): boolean {
    if (options.equals) return options.equals(a, b);
    const equalsPropName = useObjectEquals(a);
    if (equalsPropName) {
      return a[equalsPropName](b);
    }
    // eslint-disable-next-line eqeqeq
    return a == b;
  }

  function useObjectEquals(a: any) {
    if (options.ignoreOctave && utils.isObject(a) && (a as any).equalsIgnoreOctave) {
      return 'equalsIgnoreOctave';
    }
    return utils.isObject(a) && (a as any).equals ? 'equals' : false;
  }

  options.adjacentEquality = options.adjacentEquality || equals;

  log('Rejecting Duplicates:');
  log('  array', array);
  log(`  numberOfQuestions: ${numberOfQuestions}`);
  log('  options', options);

  if (numberOfQuestions === 0) return [];
  if (!numberOfQuestions)
    throw new Error(
      `Cannot reject duplicates without a provided numberOfQuestions. Currently ${numberOfQuestions}`
    );

  // If there's only one required question, no need to worry about equality!
  if (numberOfQuestions === 1 && array.length) {
    return [array[Math.floor(Math.random() * array.length)]];
  }

  if (array.length < 2)
    throw new Error(`Cannot reject duplicates on an array fewer than 2 long. Array: ${array}`);

  options = options || {};

  // If there are enough unique elements, we should return from those without
  // duplication
  const arrayOfUniques = utils.unique(utils.shuffle(array), equals);
  log(`Starting array: ${array}`);
  log(`uniques: ${arrayOfUniques}`);
  if (arrayOfUniques.length >= numberOfQuestions) {
    log('Using uniques!');
    return utils
      .avoidAdjacentUniques(arrayOfUniques, options.adjacentEquality)
      .slice(0, numberOfQuestions);
  }

  // Otherwise, repeat the uniques as needed:
  const largeEnoughArray = [].concat(arrayOfUniques);
  while (largeEnoughArray.length < numberOfQuestions) {
    const elsNeeded = numberOfQuestions - largeEnoughArray.length;
    const arrToPush = utils.shuffle(arrayOfUniques).slice(0, elsNeeded);
    largeEnoughArray.push(...arrToPush);
  }

  return utils.avoidAdjacentUniques(largeEnoughArray, options.adjacentEquality);
}
